import React, { useState, useEffect, useMemo, useCallback } from "react";
import apiClient from "@/api/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarClock, RefreshCw, UserX, ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDateEU } from "@/utils/dateFormat";
import { useAuth } from "@/contexts/AuthContext";

const filterLabels = { all: "Tous", j30: "J-30", j15: "J-15", expired: "Expirés", domiciliation: "Domiciliation", suspensions: "Suspensions" };
const colorClasses = { red: "crm-row-red", orange: "crm-row-orange", yellow: "crm-row-yellow", gray: "crm-row-gray", none: "" };

const SortIcon = ({ field, sortBy, sortDir }) => {
  if (sortBy !== field) return <ArrowUpDown size={12} className="text-[#c1c9d0] ml-1 inline" />;
  return sortDir === "asc"
    ? <ArrowUp size={12} className="text-[#3899ec] ml-1 inline" />
    : <ArrowDown size={12} className="text-[#3899ec] ml-1 inline" />;
};

const PAYMENT_LABELS = { CARD: "Bancontact / myPOS", TRANSFER: "Virement / Facture", CASH: "Cash / Numéraire", DOMICILIATION: "Domiciliation", OTHER: "FloaPay / Autre" };
const paymentLabel = (v) => PAYMENT_LABELS[v] || v;

const ExpirationPage = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState(null); // Start with null to wait for URL param
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("days_remaining");
  const [sortDir, setSortDir] = useState("asc");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  // Initialize filter from URL or default to "all"
  useEffect(() => {
    const f = searchParams.get("filter");
    setFilter(f || "all");
  }, [searchParams]);

  const refreshData = useCallback(() => {
    if (!filter) return;
    setLoading(true);
    apiClient.get("/dashboard/expirations", { params: { filter } })
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  // Fetch data when filter changes (and is initialized)
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const toggleSubStatus = async (sub) => {
    const newStatus = sub.is_active ? "INACTIVE" : "ACTIVE";
    await apiClient.put(`/subscriptions/${sub.id}`, { subscription_status: newStatus });
    refreshData();
  };

  const handleExportDomiciliations = async () => {
    try {
      const response = await apiClient.get("/members/export/domiciliations-csv", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `domiciliations_bhealth_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error("Export error:", e); }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedData = useMemo(() => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === "days_remaining" || sortBy === "price_eur") {
        va = Number(va) || 0; vb = Number(vb) || 0;
      } else if (sortBy === "start_date" || sortBy === "end_date_effective") {
        va = va || ""; vb = vb || "";
      } else {
        va = (va || "").toString().toLowerCase();
        vb = (vb || "").toString().toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortBy, sortDir]);

  const handleRenewal = async (subId, decision) => {
    await apiClient.put(`/subscriptions/${subId}`, { renewal_decision: decision });
    const r = await apiClient.get("/dashboard/expirations", { params: { filter } });
    setData(r.data);
  };

  const handleMarkFormer = async (memberId) => {
    await apiClient.put(`/members/${memberId}`, { status: "FORMER" });
    const r = await apiClient.get("/dashboard/expirations", { params: { filter } });
    setData(r.data);
  };

  const formatDateCSV = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
    } catch { return dateStr; }
  };

  const exportCSV = useCallback(() => {
    if (!sortedData.length) return;
    const headers = ["Nom", "Prénom", "Abonnement", "Paiement", "Prix", "Début", "Échéance", "Jours restants", "Statut"];
    const rows = sortedData.map(s => [
      s.member_last_name || "",
      s.member_first_name || "",
      s.plan_name || "",
      paymentLabel(s.payment_type) || "",
      s.price_eur || 0,
      formatDateCSV(s.start_date),
      formatDateCSV(s.end_date_effective || s.end_date),
      s.days_remaining,
      s.member_status || ""
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `echeances_${filter}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedData, filter]);

  const columns = [
    { key: "member_last_name", label: "Nom" },
    { key: "member_first_name", label: "Prénom" },
    { key: "plan_name", label: "Abonnement" },
    { key: "payment_type", label: "Paiement" },
    { key: "start_date", label: "Début" },
    { key: "end_date_effective", label: "Échéance" },
    { key: "days_remaining", label: "Jours" },
    { key: "member_status", label: "Statut" },
  ];

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Échéances à traiter</h1>
          <p className="crm-page-subtitle">{data.length} abonnement{data.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && filter === "domiciliation" && (
            <Button onClick={handleExportDomiciliations} variant="outline" size="sm" data-testid="export-domiciliations-csv"
              className="flex items-center gap-2 border-[#1a2744] text-[#1a2744] hover:bg-[#f4f7fa]">
              <Download size={14} /> Export Domiciliations
            </Button>
          )}
          {isAdmin && (
            <Button onClick={exportCSV} variant="outline" size="sm" data-testid="export-csv-btn" disabled={!data.length}
              className="flex items-center gap-2 border-[#3899ec] text-[#3899ec] hover:bg-[#3899ec] hover:text-white">
              <Download size={14} /> Exporter CSV
            </Button>
          )}
        </div>
      </div>

      <div className="crm-filters">
        {Object.entries(filterLabels).map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`crm-filter-btn ${filter === key ? "crm-filter-active" : ""}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <p className="py-8 text-center text-[#577083]">Chargement...</p> : data.length === 0 ? (
        <div className="crm-empty">
          <CalendarClock size={48} className="text-[#c1c9d0] mb-3" />
          <p>Aucune échéance pour ce filtre</p>
        </div>
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)} className="cursor-pointer select-none hover:text-[#3899ec]" data-testid={`sort-${col.key}`}>
                    {col.label} <SortIcon field={col.key} sortBy={sortBy} sortDir={sortDir} />
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((s) => (
                <tr key={s.id} className={`${colorClasses[s.color] || ""}`}>
                  <td className="font-medium cursor-pointer" onClick={() => navigate(`/members/${s.member_id}`)}>{s.member_last_name}</td>
                  <td className="cursor-pointer" onClick={() => navigate(`/members/${s.member_id}`)}>{s.member_first_name}</td>
                  <td>{s.plan_name} {s.end_date_indicative && <span className="crm-badge bg-yellow-100 text-yellow-700 ml-1">Ind.</span>}</td>
                  <td>{paymentLabel(s.payment_type)}</td>
                  <td>{formatDateEU(s.start_date)}</td>
                  <td className="font-medium">{formatDateEU(s.end_date_effective || s.end_date)}</td>
                  <td className="font-bold">{s.days_remaining < 0 ? <span className="text-red-500">{s.days_remaining}</span> : <span>{s.days_remaining}</span>}</td>
                  <td>
                    <button 
                      onClick={() => toggleSubStatus(s)}
                      className={`crm-badge cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${s.is_active ? 'bg-green-100 text-green-700 hover:ring-green-300' : 'bg-red-100 text-red-600 hover:ring-red-300'}`}
                      title="Cliquez pour changer le statut"
                      data-testid={`toggle-status-${s.id}`}
                    >
                      {s.is_active ? '✓ Actif' : '✗ Inactif'}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleRenewal(s.id, "RENEWED")} title="Renouveler">
                        <RefreshCw size={14} className="text-green-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleMarkFormer(s.member_id)} title="Ancien membre">
                        <UserX size={14} className="text-gray-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExpirationPage;
